if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      connectedCallback() {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange.bind(this)
          );
          this.optionChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );
        }

        if (this.dataset.hasColorOption === 'true' && this.dataset.selectedColor) {
          this.filterByColor(this.dataset.selectedColor);
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.optionChangeUnsubscriber?.();
      }

      get sectionId() {
        return (this.dataset.sectionId || this.id.replace('MediaGallery-', '')).replace(/^quickadd-/, '');
      }

      handleVariantChange({ data }) {
        const normalize = (id) => (id || '').replace(/^quickadd-/, '');
        if (
          normalize(data.sectionId) !== this.sectionId &&
          normalize(data.sectionId) !== normalize(this.dataset.sectionId)
        ) {
          return;
        }
        if (this.dataset.hasColorOption !== 'true') return;

        let colorName = null;
        if (data.html) {
          const incomingGallery =
            data.html.querySelector(`media-gallery[id="${this.id}"]`) ||
            data.html.querySelector(`media-gallery[id="MediaGallery-${this.sectionId}"]`) ||
            data.html.querySelector('media-gallery');
          if (incomingGallery && incomingGallery.dataset.selectedColor) {
            colorName = incomingGallery.dataset.selectedColor;
          }
        }

        if (!colorName && data.variant) {
          const colorOptionIndex =
            this.dataset.colorOptionIndex !== undefined ? parseInt(this.dataset.colorOptionIndex, 10) : -1;
          if (colorOptionIndex >= 0 && data.variant.options && data.variant.options[colorOptionIndex]) {
            colorName = data.variant.options[colorOptionIndex];
          } else if (data.variant.options) {
            const slides = Array.from(this.querySelectorAll('.product__media-list > li[data-media-id]'));
            for (let opt of data.variant.options) {
              if (
                opt &&
                slides.some((s) => (s.dataset.mediaColor || '').trim().toLowerCase() === opt.trim().toLowerCase())
              ) {
                colorName = opt;
                break;
              }
            }
          }
        }

        if (colorName) {
          this.dataset.selectedColor = colorName;
          this.filterByColor(colorName, data.variant?.featured_media?.id);
        } else if (data.variant?.featured_media?.id) {
          const rawSecId = this.dataset.sectionId || this.id.replace('MediaGallery-', '');
          const activeMediaId = `${rawSecId}-${data.variant.featured_media.id}`;
          this.setActiveMedia(activeMediaId, true);
        }
      }

      handleOptionValueChange({ data: { target } }) {
        if (this.dataset.hasColorOption !== 'true') return;
        const targetProductInfo = target?.closest('product-info');
        if (targetProductInfo && !targetProductInfo.contains(this)) return;

        const optionWrapper = target?.closest('.product-form__input');
        const legendOrLabel =
          optionWrapper?.querySelector('legend, label')?.textContent?.toLowerCase() || '';
        const nameAttr = (target?.name || '').toLowerCase();
        if (
          legendOrLabel.includes('color') ||
          legendOrLabel.includes('colour') ||
          nameAttr.includes('color') ||
          nameAttr.includes('colour')
        ) {
          const colorVal = target.value || target.dataset?.optionValueName || target.textContent;
          if (colorVal) {
            const cleanedColor = colorVal.trim();
            this.dataset.selectedColor = cleanedColor;
            this.filterByColor(cleanedColor);
          }
        }
      }

      filterByColor(colorName, featuredMediaId) {
        if (!colorName || this.dataset.hasColorOption !== 'true') return;
        const targetColor = colorName.trim().toLowerCase();

        const slides = Array.from(
          this.querySelectorAll('.product__media-list > li[data-media-id]')
        );
        const thumbnails = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll('.thumbnail-list > li[data-target]'))
          : [];

        if (slides.length === 0) return;

        const matchingSlides = slides.filter((slide) => {
          const slideColor = (slide.dataset.mediaColor || '').trim().toLowerCase();
          return slideColor === targetColor;
        });

        if (matchingSlides.length > 0) {
          slides.forEach((slide) => {
            const slideColor = (slide.dataset.mediaColor || '').trim().toLowerCase();
            if (slideColor === targetColor) {
              slide.style.display = '';
              slide.classList.remove('is-hidden-by-color');
            } else {
              slide.style.display = 'none';
              slide.classList.add('is-hidden-by-color');
              slide.classList.remove('is-active', 'is-quick-view-active');
            }
          });

          thumbnails.forEach((thumb) => {
            const thumbColor = (thumb.dataset.mediaColor || '').trim().toLowerCase();
            if (thumbColor === targetColor) {
              thumb.style.display = '';
              thumb.classList.remove('is-hidden-by-color');
            } else {
              thumb.style.display = 'none';
              thumb.classList.add('is-hidden-by-color');
            }
          });
        } else {
          slides.forEach((slide) => {
            slide.style.display = '';
            slide.classList.remove('is-hidden-by-color');
          });
          thumbnails.forEach((thumb) => {
            thumb.style.display = '';
            thumb.classList.remove('is-hidden-by-color');
          });
        }

        if (this.elements.viewer && typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }
        if (this.elements.thumbnails && typeof this.elements.thumbnails.resetPages === 'function') {
          this.elements.thumbnails.resetPages();
        }

        let targetSlide = null;
        const rawSecId = this.dataset.sectionId || this.id.replace('MediaGallery-', '');
        if (featuredMediaId) {
          const activeTargetId = `${rawSecId}-${featuredMediaId}`;
          targetSlide = slides.find(
            (slide) => slide.dataset.mediaId === activeTargetId && !slide.classList.contains('is-hidden-by-color')
          );
        }
        if (!targetSlide) {
          targetSlide = slides.find((slide) => !slide.classList.contains('is-hidden-by-color'));
        }

        if (targetSlide) {
          this.setActiveMedia(targetSlide.dataset.mediaId, false);
        }

        this.dispatchEvent(
          new CustomEvent('media-gallery:color-filtered', {
            bubbles: true,
            detail: { colorName, targetSlide, featuredMediaId },
          })
        );
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item:not(.is-hidden-by-color):not([style*="display: none"])') ||
          this.elements.viewer.querySelector('.product__media-item:not(.is-hidden-by-color)') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active', 'is-quick-view-active');
        });
        activeMedia.classList.add('is-active', 'is-quick-view-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail &&
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        if (this.closest('.quick-view-modal, quick-add-modal')) {
          this.playActiveMedia(activeMedia);
          return;
        }
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion?.setAttribute('aria-hidden', false);
          if (this.elements.liveRegion && window.accessibilityStrings?.imageAvailable) {
            this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );
          }
          setTimeout(() => {
            this.elements.liveRegion?.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia?.();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
